/**
 * SendSummary Component (WhatsApp)
 *
 * Full-width footer bar shown below the campaign form. Lays out las 4
 * summary chips a la izquierda (Cuenta / Destinatarios / Segmento /
 * Mensaje) y los 2 action buttons a la derecha. En mobile apila vertical.
 *
 * Cambios vs versión anterior:
 *  - Sacamos el chip de costo (al pasar a QR es cero).
 *  - Agregamos chip "Enviado desde" con la cuenta de WA elegida.
 *  - Si los destinatarios superan SPAM_THRESHOLD mostramos un warning
 *    amarillo recomendando revisar el frequency cap en Manual de tu IA.
 */

import { Send, Clock, Loader2, Users, Tag, FileText, Smartphone, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import type { SegmentSummary, WhatsAppTemplate, WhatsAppAccount } from "../types";

const SPAM_THRESHOLD = 500;

interface SendSummaryProps {
  segment: SegmentSummary | null;
  template: WhatsAppTemplate | null;
  isValid: boolean;
  sending: boolean;
  onSend: () => void;
  onSchedule: () => void;
  account?: WhatsAppAccount;
}

export function SendSummary({
  segment,
  template,
  isValid,
  sending,
  onSend,
  onSchedule,
  account,
}: SendSummaryProps) {
  const recipientCount = segment?.lastKnownCount || 0;
  const showSpamWarning = recipientCount >= SPAM_THRESHOLD && !!account;

  return (
    <div className="space-y-3">
      {/* Spam warning */}
      {showSpamWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-xs text-amber-800">
            <span className="font-semibold">
              Envío masivo de {recipientCount.toLocaleString("es-AR")} mensajes desde un mismo número.
            </span>{" "}
            WhatsApp puede marcar la cuenta como spam si excede el rate limit
            normal. Revisá tu{" "}
            <Link
              to="/brand"
              className="underline underline-offset-2 font-semibold hover:text-amber-900"
            >
              límite de frecuencia
            </Link>{" "}
            o repartí el envío entre varias cuentas vinculadas.
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          {/* Title + summary chips (left side) */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <Send size={15} className="text-emerald-500" />
              <h3 className="font-semibold text-slate-900 text-sm">
                Resumen de envío
              </h3>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SummaryChip
                icon={Smartphone}
                label="Enviado desde"
                value={account?.displayName || "Sin cuenta"}
                valueClass={
                  account
                    ? "font-semibold text-green-700 truncate"
                    : "font-semibold text-slate-400 truncate"
                }
              />
              <SummaryChip
                icon={Users}
                label="Destinatarios"
                value={
                  segment ? segment.lastKnownCount.toLocaleString("es-AR") : "—"
                }
                valueClass="font-bold text-slate-900"
              />
              <SummaryChip
                icon={Tag}
                label="Segmento"
                value={segment?.name || "—"}
                valueClass="font-semibold text-emerald-600 truncate"
              />
              <SummaryChip
                icon={FileText}
                label="Mensaje"
                value={template?.name || "—"}
                valueClass="font-mono text-slate-900 truncate"
              />
            </div>

            {!isValid && (
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                Completá cuenta, segmento, mensaje y todas sus variables para
                habilitar el envío.
              </p>
            )}
          </div>

          {/* Vertical separator (desktop only) */}
          <div className="hidden lg:block w-px self-stretch bg-slate-100" />

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-2 lg:w-56 flex-shrink-0">
            <button
              onClick={onSend}
              disabled={!isValid || sending}
              className={`
                flex-1 lg:w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300
                ${
                  isValid && !sending
                    ? "bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-700 hover:shadow-emerald-500/40 active:scale-[0.98]"
                    : "bg-slate-100 text-slate-400 cursor-not-allowed"
                }
              `}
            >
              {sending ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={15} />
                  Enviar
                </>
              )}
            </button>

            <button
              onClick={onSchedule}
              disabled={!isValid || sending}
              className={`
                flex-1 lg:w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all
                ${
                  isValid && !sending
                    ? "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
                    : "bg-white text-slate-300 border border-slate-100 cursor-not-allowed"
                }
              `}
            >
              <Clock size={15} />
              Programar envío
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-component ─── */
interface SummaryChipProps {
  icon: typeof Users;
  label: string;
  value: string;
  valueClass?: string;
}

function SummaryChip({ icon: Icon, label, value, valueClass }: SummaryChipProps) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 min-w-0">
      <Icon size={14} className="text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-slate-500 uppercase tracking-wide leading-tight">
          {label}
        </p>
        <p className={`text-xs leading-tight mt-0.5 ${valueClass || ""}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export default SendSummary;
